import React from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowLeft, FileText } from 'lucide-react';
import { useLanguage } from '../context/LanguageContext';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import SEO from '../components/SEO';

const TermsPage = () => {
  const { language } = useLanguage();

  const content = {
    es: {
      title: 'Términos y Condiciones',
      lastUpdated: 'Última actualización: Marzo 2025',
      backHome: 'Volver al inicio',
      sections: [
        {
          title: '1. Aceptación de los Términos',
          content: `Al acceder y utilizar los servicios, productos y aplicaciones de AJ Code Labs ("nosotros", "la Empresa"), usted acepta estar sujeto a estos Términos y Condiciones. Si no está de acuerdo con alguna parte de estos términos, no podrá acceder a nuestros servicios.

Estos términos aplican a todos los usuarios, visitantes y clientes que accedan o utilicen nuestros servicios, incluyendo pero no limitado a:
• Sitio web ajcodelabs.ai
• Aplicaciones de software desarrolladas por AJ Code Labs
• Servicios de consultoría y desarrollo
• Productos digitales y herramientas`
        },
        {
          title: '2. Descripción de los Servicios',
          content: `AJ Code Labs proporciona servicios de:

**Desarrollo de Software:**
• Aplicaciones web y móviles personalizadas
• Sistemas empresariales y APIs
• Integraciones y automatizaciones

**Implementación de Inteligencia Artificial:**
• Soluciones de Machine Learning
• Chatbots y asistentes virtuales
• Procesamiento de datos y análisis predictivo

**Digitalización de Procesos:**
• Transformación digital empresarial
• Migración a la nube
• Optimización de flujos de trabajo

Cada producto o aplicación puede tener términos específicos adicionales que serán comunicados al usuario en el momento de la adquisición o uso.`
        },
        {
          title: '3. Uso Aceptable',
          content: `Al utilizar nuestros productos y servicios, usted se compromete a:

**Uso Permitido:**
• Utilizar los servicios únicamente para fines legales
• Proporcionar información precisa y actualizada
• Mantener la confidencialidad de sus credenciales de acceso
• Respetar los derechos de propiedad intelectual

**Uso Prohibido:**
• Intentar acceder sin autorización a sistemas o datos
• Distribuir malware, virus o código malicioso
• Realizar ingeniería inversa de nuestro software
• Usar los servicios para actividades ilegales o fraudulentas
• Compartir credenciales de acceso con terceros no autorizados
• Sobrecargar intencionalmente nuestros sistemas`
        },
        {
          title: '4. Licencia y Propiedad Intelectual',
          content: `**Propiedad de AJ Code Labs:**
Todo el contenido, código, diseños, marcas y materiales de nuestros productos son propiedad de AJ Code Labs o sus licenciantes y están protegidos por leyes de propiedad intelectual.

**Licencia de Uso:**
Le otorgamos una licencia limitada, no exclusiva, no transferible y revocable para usar nuestros productos según los términos acordados en cada contrato específico.

**Software Personalizado:**
Para proyectos de desarrollo a medida, la propiedad del código fuente y los entregables se definirá en el contrato de servicios correspondiente.

**Restricciones:**
• No puede copiar, modificar o distribuir nuestro software sin autorización
• No puede sublicenciar o vender acceso a nuestros productos
• No puede remover avisos de copyright o marcas registradas`
        },
        {
          title: '5. Cuentas de Usuario',
          content: `Si nuestros productos requieren crear una cuenta:

**Responsabilidades del Usuario:**
• Es responsable de mantener la confidencialidad de su cuenta
• Debe notificarnos inmediatamente cualquier uso no autorizado
• Es responsable de todas las actividades bajo su cuenta
• Debe proporcionar información veraz y actualizada

**Terminación de Cuenta:**
Nos reservamos el derecho de suspender o terminar cuentas que:
• Violen estos términos
• Permanezcan inactivas por períodos prolongados
• Sean utilizadas para actividades fraudulentas`
        },
        {
          title: '6. Pagos y Facturación',
          content: `**Servicios de Desarrollo:**
Los precios y condiciones de pago se establecen en cada propuesta o contrato de servicios individual.

**Productos y Suscripciones:**
• Los precios se mostrarán claramente antes de cualquier compra
• Los pagos se procesan a través de proveedores de pago seguros
• Las suscripciones se renuevan automáticamente salvo cancelación previa

**Política de Reembolsos:**
• Los servicios de desarrollo no son reembolsables una vez iniciados
• Los productos digitales pueden tener políticas específicas según su naturaleza
• Cualquier disputa debe comunicarse dentro de los 30 días`
        },
        {
          title: '7. Disponibilidad del Servicio',
          content: `**Disponibilidad:**
Nos esforzamos por mantener nuestros servicios disponibles 24/7, pero no garantizamos disponibilidad ininterrumpida.

**Mantenimiento:**
• Realizamos mantenimientos programados con previo aviso cuando sea posible
• Los mantenimientos de emergencia pueden realizarse sin aviso previo

**Modificaciones:**
Nos reservamos el derecho de modificar, suspender o discontinuar cualquier servicio o producto, con o sin previo aviso.`
        },
        {
          title: '8. Limitación de Responsabilidad',
          content: `**Descargo de Garantías:**
Nuestros servicios se proporcionan "tal cual" y "según disponibilidad". No garantizamos que:
• Los servicios serán ininterrumpidos o libres de errores
• Los resultados obtenidos serán precisos o confiables
• Los defectos serán corregidos

**Limitación de Daños:**
En la máxima medida permitida por la ley, AJ Code Labs no será responsable por:
• Daños indirectos, incidentales o consecuentes
• Pérdida de datos, beneficios o oportunidades de negocio
• Daños que excedan el monto pagado por los servicios

**Excepciones:**
Esta limitación no aplica donde esté prohibida por ley aplicable.`
        },
        {
          title: '9. Indemnización',
          content: `Usted acepta indemnizar y mantener indemne a AJ Code Labs, sus directores, empleados y agentes de cualquier reclamación, daño, pérdida o gasto (incluyendo honorarios legales) que surjan de:

• Su uso de nuestros servicios
• Su violación de estos términos
• Su violación de derechos de terceros
• Contenido que usted proporcione o publique`
        },
        {
          title: '10. Privacidad y Datos',
          content: `El uso de nuestros servicios también se rige por nuestra Política de Privacidad, que explica cómo recopilamos, usamos y protegemos sus datos personales.

**Datos en Aplicaciones:**
Cada aplicación o producto puede recopilar datos específicos necesarios para su funcionamiento. Los detalles se proporcionarán en la documentación de cada producto.

**Seguridad:**
Implementamos medidas de seguridad para proteger sus datos, pero no podemos garantizar seguridad absoluta.`
        },
        {
          title: '11. Comunicaciones',
          content: `Al usar nuestros servicios, acepta recibir comunicaciones de nuestra parte:

**Comunicaciones Transaccionales:**
• Confirmaciones de pedidos y facturas
• Actualizaciones de servicio importantes
• Notificaciones de seguridad

**Comunicaciones Promocionales:**
• Solo si ha dado su consentimiento explícito
• Puede darse de baja en cualquier momento

Ver nuestra página de Consentimiento SMS para detalles sobre mensajes de texto.`
        },
        {
          title: '12. Ley Aplicable y Jurisdicción',
          content: `**Ley Aplicable:**
Estos términos se rigen por las leyes de Estados Unidos de América y el estado de Florida, sin considerar sus disposiciones sobre conflicto de leyes.

**Resolución de Disputas:**
Cualquier disputa relacionada con estos términos se resolverá mediante:
1. Negociación directa entre las partes
2. Mediación si la negociación no tiene éxito
3. Arbitraje vinculante como último recurso

**Jurisdicción:**
Los tribunales de Miami, Florida, tendrán jurisdicción exclusiva para cualquier disputa que requiera resolución judicial.`
        },
        {
          title: '13. Modificaciones a los Términos',
          content: `Nos reservamos el derecho de modificar estos términos en cualquier momento. Las modificaciones entrarán en vigencia al ser publicadas en nuestro sitio web.

**Notificación:**
• Notificaremos cambios materiales por email o aviso en el sitio
• El uso continuado después de las modificaciones implica aceptación

**Historial:**
Mantenemos un registro de versiones anteriores de estos términos disponible bajo solicitud.`
        },
        {
          title: '14. Disposiciones Generales',
          content: `**Acuerdo Completo:**
Estos términos, junto con la Política de Privacidad y cualquier contrato específico, constituyen el acuerdo completo entre usted y AJ Code Labs.

**Divisibilidad:**
Si alguna disposición resulta inválida, las demás permanecerán en vigor.

**No Renuncia:**
El no ejercer algún derecho no constituye renuncia al mismo.

**Cesión:**
No puede ceder estos términos sin nuestro consentimiento. Nosotros podemos cederlos libremente.`
        },
        {
          title: '15. Contacto',
          content: `Para preguntas sobre estos Términos y Condiciones:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

Oficinas:
• Miami, FL - Estados Unidos
• Quito - Ecuador
• Londres - Reino Unido

Tiempo de respuesta: menos de 48 horas hábiles.`
        }
      ]
    },
    en: {
      title: 'Terms and Conditions',
      lastUpdated: 'Last updated: March 2025',
      backHome: 'Back to home',
      sections: [
        {
          title: '1. Acceptance of Terms',
          content: `By accessing and using the services, products, and applications of AJ Code Labs ("we", "the Company"), you agree to be bound by these Terms and Conditions. If you do not agree with any part of these terms, you may not access our services.

These terms apply to all users, visitors, and customers who access or use our services, including but not limited to:
• Website ajcodelabs.ai
• Software applications developed by AJ Code Labs
• Consulting and development services
• Digital products and tools`
        },
        {
          title: '2. Description of Services',
          content: `AJ Code Labs provides services including:

**Software Development:**
• Custom web and mobile applications
• Enterprise systems and APIs
• Integrations and automations

**Artificial Intelligence Implementation:**
• Machine Learning solutions
• Chatbots and virtual assistants
• Data processing and predictive analytics

**Process Digitalization:**
• Business digital transformation
• Cloud migration
• Workflow optimization

Each product or application may have additional specific terms that will be communicated to the user at the time of acquisition or use.`
        },
        {
          title: '3. Acceptable Use',
          content: `When using our products and services, you agree to:

**Permitted Use:**
• Use services only for lawful purposes
• Provide accurate and up-to-date information
• Maintain the confidentiality of your access credentials
• Respect intellectual property rights

**Prohibited Use:**
• Attempting unauthorized access to systems or data
• Distributing malware, viruses, or malicious code
• Reverse engineering our software
• Using services for illegal or fraudulent activities
• Sharing access credentials with unauthorized third parties
• Intentionally overloading our systems`
        },
        {
          title: '4. License and Intellectual Property',
          content: `**AJ Code Labs Ownership:**
All content, code, designs, trademarks, and materials in our products are owned by AJ Code Labs or its licensors and are protected by intellectual property laws.

**License to Use:**
We grant you a limited, non-exclusive, non-transferable, and revocable license to use our products according to the terms agreed upon in each specific contract.

**Custom Software:**
For custom development projects, ownership of source code and deliverables will be defined in the corresponding service contract.

**Restrictions:**
• You may not copy, modify, or distribute our software without authorization
• You may not sublicense or sell access to our products
• You may not remove copyright notices or trademarks`
        },
        {
          title: '5. User Accounts',
          content: `If our products require creating an account:

**User Responsibilities:**
• You are responsible for maintaining the confidentiality of your account
• You must notify us immediately of any unauthorized use
• You are responsible for all activities under your account
• You must provide truthful and up-to-date information

**Account Termination:**
We reserve the right to suspend or terminate accounts that:
• Violate these terms
• Remain inactive for extended periods
• Are used for fraudulent activities`
        },
        {
          title: '6. Payments and Billing',
          content: `**Development Services:**
Prices and payment conditions are established in each individual proposal or service contract.

**Products and Subscriptions:**
• Prices will be clearly displayed before any purchase
• Payments are processed through secure payment providers
• Subscriptions renew automatically unless previously cancelled

**Refund Policy:**
• Development services are non-refundable once started
• Digital products may have specific policies according to their nature
• Any dispute must be communicated within 30 days`
        },
        {
          title: '7. Service Availability',
          content: `**Availability:**
We strive to maintain our services available 24/7, but we do not guarantee uninterrupted availability.

**Maintenance:**
• We perform scheduled maintenance with prior notice when possible
• Emergency maintenance may be performed without prior notice

**Modifications:**
We reserve the right to modify, suspend, or discontinue any service or product, with or without prior notice.`
        },
        {
          title: '8. Limitation of Liability',
          content: `**Disclaimer of Warranties:**
Our services are provided "as is" and "as available." We do not guarantee that:
• Services will be uninterrupted or error-free
• Results obtained will be accurate or reliable
• Defects will be corrected

**Limitation of Damages:**
To the maximum extent permitted by law, AJ Code Labs shall not be liable for:
• Indirect, incidental, or consequential damages
• Loss of data, profits, or business opportunities
• Damages exceeding the amount paid for services

**Exceptions:**
This limitation does not apply where prohibited by applicable law.`
        },
        {
          title: '9. Indemnification',
          content: `You agree to indemnify and hold harmless AJ Code Labs, its directors, employees, and agents from any claim, damage, loss, or expense (including legal fees) arising from:

• Your use of our services
• Your violation of these terms
• Your violation of third-party rights
• Content you provide or publish`
        },
        {
          title: '10. Privacy and Data',
          content: `The use of our services is also governed by our Privacy Policy, which explains how we collect, use, and protect your personal data.

**Data in Applications:**
Each application or product may collect specific data necessary for its operation. Details will be provided in each product's documentation.

**Security:**
We implement security measures to protect your data, but we cannot guarantee absolute security.`
        },
        {
          title: '11. Communications',
          content: `By using our services, you agree to receive communications from us:

**Transactional Communications:**
• Order confirmations and invoices
• Important service updates
• Security notifications

**Promotional Communications:**
• Only if you have given explicit consent
• You can unsubscribe at any time

See our SMS Consent page for details on text messages.`
        },
        {
          title: '12. Applicable Law and Jurisdiction',
          content: `**Applicable Law:**
These terms are governed by the laws of the United States of America and the state of Florida, without regard to its conflict of law provisions.

**Dispute Resolution:**
Any dispute related to these terms will be resolved through:
1. Direct negotiation between the parties
2. Mediation if negotiation is unsuccessful
3. Binding arbitration as a last resort

**Jurisdiction:**
The courts of Miami, Florida, shall have exclusive jurisdiction for any dispute requiring judicial resolution.`
        },
        {
          title: '13. Modifications to Terms',
          content: `We reserve the right to modify these terms at any time. Modifications will take effect upon being posted on our website.

**Notification:**
• We will notify material changes by email or site notice
• Continued use after modifications implies acceptance

**History:**
We maintain a record of previous versions of these terms available upon request.`
        },
        {
          title: '14. General Provisions',
          content: `**Entire Agreement:**
These terms, together with the Privacy Policy and any specific contracts, constitute the entire agreement between you and AJ Code Labs.

**Severability:**
If any provision is found invalid, the remaining provisions will remain in effect.

**No Waiver:**
Failure to exercise any right does not constitute a waiver of that right.

**Assignment:**
You may not assign these terms without our consent. We may freely assign them.`
        },
        {
          title: '15. Contact',
          content: `For questions about these Terms and Conditions:

**AJ Code Labs**
Email: contacto@ajcodelabs.ai

Offices:
• Miami, FL - United States
• Quito - Ecuador
• London - United Kingdom

Response time: less than 48 business hours.`
        }
      ]
    }
  };

  const t = content[language];

  return (
    <main className="min-h-screen bg-[#0A0A0B]">
      <SEO 
        title={t.title}
        description={language === 'es' 
          ? 'Términos y condiciones de uso de los productos y servicios de AJ Code Labs.'
          : 'Terms and conditions for using AJ Code Labs products and services.'}
        url="/terms"
      />
      
      <Navbar />

      {/* Hero Section */}
      <section className="pt-32 pb-16 relative overflow-hidden bg-[#FAFAFA]">
        <div className="container-custom relative z-10">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-[#52525B] hover:text-[#E03A1C] transition-colors mb-8 group"
            data-testid="back-home-link"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-1 transition-transform" />
            {t.backHome}
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex items-center gap-4 mb-6"
          >
            <div className="p-3 bg-[#E03A1C] rounded-xl">
              <FileText className="w-8 h-8 text-[#FAFAFA]" />
            </div>
            <div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-semibold text-[#0A0A0B] tracking-tight">
                {t.title}
              </h1>
              <p className="text-[#52525B] mt-2">{t.lastUpdated}</p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24 bg-[#FAFAFA]">
        <div className="container-custom">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="max-w-4xl mx-auto"
          >
            <div className="bg-white rounded-2xl p-8 md:p-12 border border-[#E4E4E7]">
              {t.sections.map((section, index) => (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ delay: index * 0.05 }}
                  className={index !== t.sections.length - 1 ? 'mb-10 pb-10 border-b border-[#E4E4E7]' : ''}
                >
                  <h2 className="text-xl md:text-2xl font-semibold text-[#0A0A0B] mb-4 tracking-tight">
                    {section.title}
                  </h2>
                  <div className="text-[#52525B] leading-relaxed whitespace-pre-line">
                    {section.content.split('**').map((part, i) => 
                      i % 2 === 1 ? (
                        <strong key={i} className="text-[#0A0A0B] font-semibold">{part}</strong>
                      ) : (
                        <span key={i}>{part}</span>
                      )
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      <Footer />
    </main>
  );
};

export default TermsPage;
